import React from "react";
import _ from "lodash";
import classnames from "classnames";
import { FormattedMessage } from "react-intl";

import { ArrowUpward, ArrowDownward } from "components/Icons";
import { IMeta, IColumn } from "models/.";
import Checkbox from "components/Checkbox";
import Pagination from "./partials/Pagination";
import { getFormatted } from "helpers/formatting";

import "./style.scss";

interface ICheckedRows {
  [key: string]: boolean;
}

interface IProps {
  columns: IColumn[];
  dataSource: any[];
  onRowClick?: (any?) => void;
  isLoading?: boolean;
  meta?: IMeta;
  onQueryChange?: (meta?: IMeta) => void;
  onRowSelect?: (checkedRows: ICheckedRows) => void;
  checkedRows?: ICheckedRows;
  keyPath?: string;
  readPath?: string;
  groupBy?: string;
  pageName?: string;
}

interface IDefaultProps {
  readonly readPath: string;
  readonly keyPath: string;
}

interface IState {
  scrolled: boolean;
  hoveredRow: string | null;
  isMobile: boolean;
}

class Table extends React.Component<IProps, IState> {
  private mediaQuery: MediaQueryList | null = null;

  constructor(props: any) {
    super(props);

    this.state = {
      scrolled: false,
      hoveredRow: null,
      isMobile: typeof window !== "undefined" && window.innerWidth <= 768,
    };
  }

  public componentDidMount() {
    if (typeof window !== "undefined") {
      this.mediaQuery = window.matchMedia("(max-width: 768px)");
      this.mediaQuery.addEventListener("change", this.handleMediaChange);
    }
  }

  public componentWillUnmount() {
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener("change", this.handleMediaChange);
    }
  }

  private handleMediaChange = (e: MediaQueryListEvent) => {
    this.setState({ isMobile: e.matches });
  };

  private static defaultProps: IDefaultProps = {
    readPath: "Read",
    keyPath: "Id",
  };

  public renderTitle = (title, orderByType) => (
    <span className="header-text">
      {title && <FormattedMessage id={title} />}
      {orderByType &&
        (orderByType === "asc" ? (
          <ArrowDownward className="arrow-icons" />
        ) : (
          <ArrowUpward className="arrow-icons" />
        ))}
    </span>
  );

  public onThClick = (key, isSortable) => {
    const { meta = {}, onQueryChange } = this.props;

    if (isSortable) {
      const { orderByKey = "", orderByType = "" } = meta;
      const type = orderByKey === key && orderByType === "asc" ? "desc" : "asc";

      onQueryChange &&
        onQueryChange({
          ...meta,
          orderByKey: key,
          orderByType: type,
          currentPage: 0,
        });
    }
  };

  public getFixedRowStyle = (index, widths) =>
    widths.length > 0
      ? {
          left: widths[index - 1] - 16 || 0,
          width: widths[index] - 32 + "px",
          paddingRight: 16,
        }
      : {};

  private toggleAllCheckboxes = (checked, halfChecked) => {
    const {
      dataSource,
      onRowSelect = _.noop,
      keyPath,
      checkedRows = {},
    } = this.props;

    if (halfChecked) {
      onRowSelect({});
    } else {
      const tempArray = _.cloneDeep(checkedRows);

      _.forEach(dataSource, (rowData) => {
        tempArray[_.get(rowData, keyPath!)] = checked;
      });
      const onlyCheckedRows = _.pickBy(tempArray, _.identity);
      onRowSelect && onRowSelect(onlyCheckedRows);
    }
  };

  private toggleCheckbox = (key, checked) => {
    const { onRowSelect, checkedRows } = this.props;

    const onlyCheckedRows = _.pickBy(
      { ...checkedRows, [key]: checked } as ICheckedRows,
      _.identity
    );
    onRowSelect && onRowSelect(onlyCheckedRows);
  };

  private renderTableHead(columns, widths) {
    const { checkedRows = {}, dataSource, keyPath, meta = {} } = this.props;
    const isAllChecked = _.every(
      dataSource,
      (item) => checkedRows[_.get(item, keyPath!, -1)]
    );
    const halfChecked = !isAllChecked && !_.isEmpty(checkedRows);
    const { orderByKey, orderByType } = meta;

    return (
      <thead>
        <tr className="header-row">
          {columns.map(({ key, title, type, isSortable = true }, index) => (
            <th
              key={_.uniqueId(key)}
              onClick={() => this.onThClick(key, isSortable)}
              className={classnames({
                ordered: orderByKey === key,
                "right-aligned": type === "currency",
              })}
              style={this.getFixedRowStyle(index, widths)}
            >
              {key === "checkbox" ? (
                <Checkbox
                  borderColor="#CDD2D6"
                  halfChecked={halfChecked}
                  checked={isAllChecked || halfChecked}
                  onChange={(checked) =>
                    this.toggleAllCheckboxes(checked, halfChecked)
                  }
                />
              ) : (
                this.renderTitle(title, orderByKey === key && orderByType)
              )}
            </th>
          ))}
        </tr>
      </thead>
    );
  }

  private renderGroupedTableBody(columns, widths) {
    const { dataSource, groupBy = "" } = this.props;
    const groupedData = _.groupBy(dataSource, groupBy);

    return (
      <tbody>
        {_.map(groupedData, (data, dataKey) => (
          <React.Fragment key={dataKey}>
            <tr>
              <td colSpan={columns.length} className="group-header">
                {widths.length ? dataKey : ""}
              </td>
            </tr>
            {this.renderTableRows(data, columns, widths, dataKey)}
          </React.Fragment>
        ))}
      </tbody>
    );
  }

  private renderTableRows = (dataSource, columns, widths, dataKey = "") => {
    const { onRowClick, checkedRows = {}, readPath, keyPath } = this.props;
    const { hoveredRow } = this.state;

    return (
      <>
        {dataSource.map((rowData, rowIndex) => (
          <tr
            key={rowIndex}
            className={classnames({
              hovered: hoveredRow === dataKey + rowIndex,
              clickable: !!onRowClick,
              unread: !_.get(rowData, readPath!, true),
            })}
            onMouseEnter={() =>
              this.setState({ hoveredRow: dataKey + rowIndex })
            }
            onMouseLeave={() => this.setState({ hoveredRow: null })}
          >
            {columns.map(({ key, render, type, notRowClick }) => (
              <td
                key={_.uniqueId(key)}
                style={this.getFixedRowStyle(rowIndex, widths)}
                className={type === "currency" ? "right-aligned" : ""}
                onClick={() =>
                  !notRowClick && onRowClick && onRowClick(rowData)
                }
              >
                {render ? (
                  render(rowData, rowIndex)
                ) : key === "checkbox" ? (
                  <Checkbox
                    checked={!!checkedRows[_.get(rowData, keyPath!)]}
                    onChange={(checked) =>
                      this.toggleCheckbox(_.get(rowData, keyPath!), checked)
                    }
                  />
                ) : (
                  <span className="header-text">
                    {getFormatted(_.get(rowData, key), type)}
                  </span>
                )}
              </td>
            ))}
          </tr>
        ))}
      </>
    );
  };

  public render() {
    const {
      columns,
      isLoading,
      meta,
      onQueryChange,
      dataSource,
      groupBy,
      pageName,
    } = this.props;
    const mobile = this.state.isMobile;
    const fixed = mobile
      ? []
      : columns.filter((col) => (col.fixedWidth || 0) > 0 && col.visible);
    const scrolling = mobile
      ? columns.filter((col) => col.visible)
      : columns.filter((col) => !col.fixedWidth && col.visible);
    const widths = fixed.map((col) => col.fixedWidth);
    const totalWidthStyle = _.sum(widths);

    if (isLoading) {
      return <div className="loading" />;
    }

    return (
      <div>
        <div
          className={classnames("cnb-table", {
            fixed: !mobile && totalWidthStyle !== 0,
          })}
        >
          {dataSource && dataSource.length > 0 ? (
            <div
              className={classnames("wrapper", {
                scrolled: this.state.scrolled,
              })}
              style={
                mobile
                  ? undefined
                  : { marginLeft: `${totalWidthStyle && totalWidthStyle}px` }
              }
              onScroll={(e) => {
                this.setState({ scrolled: !!e.currentTarget.scrollLeft });
              }}
            >
              {!mobile && totalWidthStyle > 0 && (
                <table
                  className="fixed"
                  style={{ width: `${totalWidthStyle}px` }}
                >
                  {this.renderTableHead(fixed, widths)}
                  {groupBy ? (
                    this.renderGroupedTableBody(fixed, widths)
                  ) : (
                    <tbody>
                      {this.renderTableRows(dataSource, fixed, widths, "")}
                    </tbody>
                  )}
                </table>
              )}
              <table>
                {this.renderTableHead(scrolling, [])}
                {groupBy ? (
                  this.renderGroupedTableBody(scrolling, [])
                ) : (
                  <tbody>
                    {this.renderTableRows(dataSource, scrolling, [], "")}
                  </tbody>
                )}
              </table>
            </div>
          ) : (
            <h2 className="message">
              <FormattedMessage id="NO_DATA" />
            </h2>
          )}
        </div>
        {meta && (
          <Pagination
            onPageChange={(newMeta) =>
              onQueryChange && onQueryChange({ ...meta, ...newMeta })
            }
            meta={meta}
            pageName={pageName}
          />
        )}
      </div>
    );
  }
}

export default Table;
